import React, { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { challengeAPI, examAPI, subjectAPI, getErrorMessage } from '../lib/api';
import {
  Users, Plus, Crown, Swords, Share2, Copy, Check, Clock, Trophy, Zap, 
  Link2, Play, Loader2, UserPlus, Lock, X, Trash2, Medal, Target, BarChart3, Award, ChevronRight
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';

const Challenges = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [exams, setExams] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [joinCode, setJoinCode] = useState('');
  const [joiningCode, setJoiningCode] = useState(false);
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [chapters, setChapters] = useState({});  // {subjectId: [chapters]}
  const [loadingChapters, setLoadingChapters] = useState({});  // {subjectId: boolean}
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());  // subjects with chapters visible

  // Create challenge form state
  const [formData, setFormData] = useState({
    exam_id: '',
    title: '',
    num_questions: 10,
    duration_minutes: 30,
    subject_ids: [],
    chapter_ids: [],
    difficulty: 'mixed',
    question_source: 'mixed'
  });

  const isPremium = user?.premium;

  useEffect(() => {
    if (isAuthenticated) {
      loadChallenges();
      loadExams();
    }
  }, [isAuthenticated]);

  const loadChallenges = async () => {
    try {
      const response = await challengeAPI.getAll();
      const challengesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.challenges || []);
      setChallenges(challengesData);
    } catch (error) {
      logger.error('Failed to load challenges:', error);
      setChallenges([]);
    } finally {
      setLoading(false);
    }
  };

  const loadExams = async () => {
    try {
      const response = await examAPI.getAll();
      const examsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.exams || []);
      setExams(examsData);
    } catch (error) {
      logger.error('Failed to load exams:', error);
      setExams([]);
    }
  };

  const loadSubjects = async (examId) => {
    if (!examId) {
      setSubjects([]);
      return;
    }
    setLoadingSubjects(true);
    try {
      const response = await api.get(`/exams/${examId}/subjects`);
      setSubjects(response.data || []);
    } catch (error) {
      logger.error('Failed to load subjects:', error);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Load subjects when exam changes
  useEffect(() => {
    if (formData.exam_id) {
      loadSubjects(formData.exam_id);
      // Reset subject selection when exam changes
      setFormData(prev => ({ ...prev, subject_ids: [] }));
    }
  }, [formData.exam_id]);

  const toggleSubject = (subjectId) => {
    setFormData(prev => {
      const isRemoving = prev.subject_ids.includes(subjectId);
      const newSubjectIds = isRemoving
        ? prev.subject_ids.filter(id => id !== subjectId)
        : [...prev.subject_ids, subjectId];
      
      // If removing subject, also remove its chapters from selection
      let newChapterIds = prev.chapter_ids;
      if (isRemoving && chapters[subjectId]) {
        const chapterIdsToRemove = chapters[subjectId].map(c => c.id);
        newChapterIds = prev.chapter_ids.filter(id => !chapterIdsToRemove.includes(id));
      }
      
      return {
        ...prev,
        subject_ids: newSubjectIds,
        chapter_ids: newChapterIds
      };
    });
  };

  const loadChaptersForSubject = async (subjectId) => {
    if (chapters[subjectId]) return; // Already loaded
    
    setLoadingChapters(prev => ({ ...prev, [subjectId]: true }));
    try {
      const response = await subjectAPI.getChapters(subjectId);
      setChapters(prev => ({ ...prev, [subjectId]: response.data || [] }));
    } catch (error) {
      logger.error('Failed to load chapters:', error);
      setChapters(prev => ({ ...prev, [subjectId]: [] }));
    } finally {
      setLoadingChapters(prev => ({ ...prev, [subjectId]: false }));
    }
  };

  const toggleExpandSubject = async (subjectId) => {
    // Load chapters if not loaded yet
    if (!chapters[subjectId]) {
      await loadChaptersForSubject(subjectId);
    }
    
    setExpandedSubjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subjectId)) {
        newSet.delete(subjectId);
      } else {
        newSet.add(subjectId);
      }
      return newSet;
    });
  };

  const toggleChapter = (chapterId) => {
    setFormData(prev => ({
      ...prev,
      chapter_ids: prev.chapter_ids.includes(chapterId)
        ? prev.chapter_ids.filter(id => id !== chapterId)
        : [...prev.chapter_ids, chapterId]
    }));
  };

  const toggleAllChaptersForSubject = (subjectId) => {
    const subjectChapters = chapters[subjectId] || [];
    const chapterIds = subjectChapters.map(c => c.id);
    const allSelected = chapterIds.every(id => formData.chapter_ids.includes(id));
    
    setFormData(prev => ({
      ...prev,
      chapter_ids: allSelected
        ? prev.chapter_ids.filter(id => !chapterIds.includes(id))
        : [...new Set([...prev.chapter_ids, ...chapterIds])]
    }));
  };

  const getSelectedExam = () => {
    return exams.find(e => e.id === formData.exam_id);
  };

  const handleCreateChallenge = async () => {
    if (!isPremium) {
      toast.error('🔒 This feature is for premium users only');
      navigate('/subscription');
      return;
    }

    if (!formData.exam_id) {
      toast.error('Please select an exam');
      return;
    }

    setCreatingChallenge(true);
    try {
      const response = await challengeAPI.create({
        exam_id: formData.exam_id,
        title: formData.title,
        num_questions: formData.num_questions,
        duration_minutes: formData.duration_minutes,
        subject_ids: formData.subject_ids,
        chapter_ids: formData.chapter_ids,
        difficulty: formData.difficulty,
        question_source: formData.question_source
      });

      toast.success('Challenge created! Share the code with friends.');
      
      // Navigate to the challenge lobby
      navigate(`/challenge/${response.data.code}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create challenge'));
    } finally {
      setCreatingChallenge(false);
    }
  };

  const handleCopyLink = (challengeCode) => {
    const link = `${window.location.origin}/challenge/${challengeCode}`;
    navigator.clipboard.writeText(link);
    setCopiedId(challengeCode);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(code);
    toast.success('Code copied!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleJoinByCode = async () => {
    if (!joinCode.trim()) {
      toast.error('Please enter a challenge code');
      return;
    }

    setJoiningCode(true);
    try {
      await challengeAPI.join(joinCode.toUpperCase());
      toast.success('Joined challenge!');
      navigate(`/challenge/${joinCode.toUpperCase()}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to join challenge'));
    } finally {
      setJoiningCode(false);
    }
  };

  const handleViewChallenge = (code) => {
    navigate(`/challenge/${code}`);
  };

  const handleViewCompletedStats = (challenge) => {
    setSelectedChallenge(challenge);
    setShowStatsModal(true);
  };

  const getUserResult = (challenge) => {
    if (!challenge.participants || !user) return null;
    return challenge.participants.find(p => p.user_id === user.id);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="text-yellow-500" size={20} />;
    if (rank === 2) return <Medal className="text-gray-400" size={20} />;
    if (rank === 3) return <Medal className="text-amber-600" size={20} />;
    return <span className="text-sm font-bold text-muted-foreground">#{rank}</span>;
  };

  // Separate active and completed challenges
  const activeChallenges = challenges.filter(c => c.status !== 'completed');
  const completedChallenges = challenges.filter(c => c.status === 'completed');

  const handleDeleteChallenge = async (challengeId, code) => {
    if (!window.confirm('Are you sure you want to delete this challenge? This cannot be undone.')) {
      return;
    }
    try {
      await challengeAPI.cancel(code);
      toast.success('Challenge deleted');
      setChallenges(challenges.filter(c => c.code !== code));
    } catch (error) {
      // Handle error - detail can be string or array of validation errors
      let errorMessage = 'Failed to delete challenge';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d.message || String(d)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || 'Failed to delete challenge';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'waiting':
        return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">Waiting</Badge>;
      case 'starting':
        return <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">Starting</Badge>;
      case 'active':
        return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Live</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Completed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Crown className="text-yellow-500 mx-auto mb-4" size={64} />
          <h1 className="text-3xl font-bold text-foreground dark:text-white mb-4">
            Challenge Friends
          </h1>
          <p className="text-muted-foreground mb-8">
            This feature is exclusively for premium members. Challenge your friends to take exams together and compete on the leaderboard.
          </p>
          <Button
            onClick={() => navigate('/subscription')}
            className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white"
          >
            <Crown size={20} className="mr-2" />
            Upgrade to Premium
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
                <Swords className="text-white" size={32} />
              </div>
              <div>
                <h1 className="text-4xl font-heading font-bold text-foreground dark:text-white">
                  Challenge Friends
                </h1>
                <p className="text-muted-foreground">Create or join multiplayer challenges</p>
              </div>
            </div>
          </div>
        </div>

        {/* Join by Code Section */}
        <Card className="p-6 mb-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 rounded-2xl border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <UserPlus className="text-white" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-foreground dark:text-white mb-1">Join a Challenge</h3>
              <p className="text-sm text-muted-foreground">Enter the 6-character code shared by your friend</p>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter code (e.g. ABC123)"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="w-40 uppercase font-mono text-center tracking-wider"
                maxLength={6}
              />
              <Button 
                onClick={handleJoinByCode} 
                disabled={joiningCode || !joinCode.trim()}
                className="gap-2"
              >
                {joiningCode ? <Loader2 className="animate-spin" size={18} /> : <Play size={18} />}
                Join
              </Button>
            </div>
          </div>
        </Card>

        {/* Create Challenge Section */}
        {!showCreate ? (
          <Card className="p-6 mb-8 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => setShowCreate(true)}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                <Plus className="text-white" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground dark:text-white">Create New Challenge</h3>
                <p className="text-sm text-muted-foreground">Set up a challenge and invite friends via a shareable link</p>
              </div>
              <Button className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600">
                <Plus size={18} />
                Create
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-8 mb-8 bg-card rounded-3xl border border-border">
            <h2 className="text-2xl font-bold text-foreground dark:text-white mb-6 flex items-center gap-3">
              <Swords size={28} className="text-purple-500" />
              Create New Challenge
            </h2>

            <div className="space-y-6">
              {/* Challenge Title */}
              <div>
                <label className="block text-sm font-semibold text-foreground dark:text-white mb-2">
                  Challenge Title (Optional)
                </label>
                <Input
                  placeholder="e.g. Physics Showdown, Weekend Battle..."
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-input text-foreground dark:bg-gray-900/50 dark:border-gray-700 dark:text-white"
                />
              </div>

              {/* Exam Selection */}
              <div>
                <label className="block text-sm font-semibold text-foreground dark:text-white mb-2">
                  Select Exam *
                </label>
                <select
                  value={formData.exam_id}
                  onChange={(e) => setFormData({ ...formData, exam_id: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground dark:bg-gray-900 dark:border-gray-700"
                >
                  <option value="">Choose an exam...</option>
                  {exams.filter(exam => exam.is_available).map(exam => (
                    <option key={exam.id} value={exam.id}>{exam.name}</option>
                  ))}
                  <option disabled className="text-muted-foreground">── Coming Soon ──</option>
                  {exams.filter(exam => !exam.is_available).map(exam => (
                    <option key={exam.id} value={exam.id} disabled>🔒 {exam.name}</option>
                  ))}
                </select>
              </div>

              {/* Subject Selection - Shows when exam is selected */}
              {formData.exam_id && (
                <div>
                  <label className="block text-sm font-semibold text-foreground dark:text-white mb-2">
                    Select Subjects {subjects.length > 0 && <span className="text-muted-foreground font-normal">(optional, leave empty for all)</span>}
                  </label>
                  {loadingSubjects ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="animate-spin" size={16} />
                      Loading subjects...
                    </div>
                  ) : subjects.length > 0 ? (
                    <div className="space-y-3">
                      {subjects.map(subject => (
                        <div key={subject.id} className="border border-border rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => toggleSubject(subject.id)}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                formData.subject_ids.includes(subject.id)
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                              }`}
                            >
                              {subject.name}
                            </button>
                            {formData.subject_ids.includes(subject.id) && (
                              <button
                                onClick={() => toggleExpandSubject(subject.id)}
                                className="text-sm text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1"
                              >
                                {expandedSubjects.has(subject.id) ? 'Hide' : 'Select'} Chapters
                                <ChevronRight size={14} className={`transition-transform ${expandedSubjects.has(subject.id) ? 'rotate-90' : ''}`} />
                              </button>
                            )}
                          </div>
                          
                          {/* Chapter Selection - Shows when subject is selected and expanded */}
                          {formData.subject_ids.includes(subject.id) && expandedSubjects.has(subject.id) && (
                            <div className="mt-3 pt-3 border-t border-border">
                              {loadingChapters[subject.id] ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Loader2 className="animate-spin" size={14} />
                                  Loading chapters...
                                </div>
                              ) : chapters[subject.id]?.length > 0 ? (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-muted-foreground">
                                      {formData.chapter_ids.filter(id => chapters[subject.id]?.some(c => c.id === id)).length} / {chapters[subject.id].length} selected
                                    </span>
                                    <button
                                      onClick={() => toggleAllChaptersForSubject(subject.id)}
                                      className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                                    >
                                      {chapters[subject.id].every(c => formData.chapter_ids.includes(c.id)) ? 'Deselect All' : 'Select All'}
                                    </button>
                                  </div>
                                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                                    {chapters[subject.id].map(chapter => (
                                      <button
                                        key={chapter.id}
                                        onClick={() => toggleChapter(chapter.id)}
                                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                                          formData.chapter_ids.includes(chapter.id)
                                            ? 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border border-purple-500'
                                            : 'bg-muted/50 text-muted-foreground hover:bg-muted border border-transparent'
                                        }`}
                                      >
                                        {chapter.name}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground">No chapters found</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No subjects found for this exam</p>
                  )}
                </div>
              )}

              {/* Difficulty & Question Source */}
              {formData.exam_id && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-foreground dark:text-white mb-2">
                      Difficulty
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'mixed', label: 'Mixed' },
                        { key: 'easy', label: '🟢 Easy' },
                        { key: 'medium', label: '🟡 Medium' },
                        { key: 'hard', label: '🔴 Hard' }
                      ].map(d => (
                        <button
                          key={d.key}
                          onClick={() => setFormData({ ...formData, difficulty: d.key })}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                            formData.difficulty === d.key
                              ? 'border-purple-500 bg-purple-500/10 text-purple-600 dark:text-purple-400'
                              : 'border-border hover:border-purple-500/50'
                          }`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground dark:text-white mb-2">
                      Question Source
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'mixed', label: 'Mixed' },
                        { key: 'pyq', label: '📖 PYQ' },
                        { key: 'important', label: '⭐ Important' }
                      ].map(s => (
                        <button
                          key={s.key}
                          onClick={() => setFormData({ ...formData, question_source: s.key })}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                            formData.question_source === s.key
                              ? 'border-purple-500 bg-purple-500/10 text-purple-600 dark:text-purple-400'
                              : 'border-border hover:border-purple-500/50'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Questions & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground dark:text-white mb-2">
                    Number of Questions
                  </label>
                  <Input
                    type="number"
                    min="5"
                    max="50"
                    value={formData.num_questions}
                    onChange={(e) => setFormData({ ...formData, num_questions: parseInt(e.target.value) || 10 })}
                    className="bg-input text-foreground dark:bg-gray-900/50 dark:border-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground dark:text-white mb-2">
                    Duration (minutes)
                  </label>
                  <Input
                    type="number"
                    min="5"
                    max="180"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 30 })}
                    className="bg-input text-foreground dark:bg-gray-900/50 dark:border-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <Link2 className="text-blue-500 mt-0.5" size={20} />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      How it works
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      After creating, you'll get a unique code to share with friends. 
                      Once everyone joins the lobby, you can start the challenge. 
                      Everyone takes the test together with a 5-second countdown.
                    </p>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleCreateChallenge}
                  disabled={creatingChallenge || !formData.exam_id}
                  className="flex-1 gap-2 bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  {creatingChallenge ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Swords size={18} />
                  )}
                  Create & Get Link
                </Button>
                <Button
                  onClick={() => setShowCreate(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Active Challenges */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-foreground dark:text-white mb-6">
            Active Challenges
          </h2>

          {loading ? (
            <LoadingSpinner text="Loading challenges..." />
          ) : activeChallenges.length === 0 ? (
            <Card className="p-12 text-center bg-card rounded-3xl border border-border">
              <Swords className="text-muted-foreground mx-auto mb-4" size={48} />
              <p className="text-muted-foreground mb-4">No active challenges</p>
              <p className="text-sm text-muted-foreground mb-6">Create one and invite friends, or join using a code.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeChallenges.map(challenge => (
                <Card key={challenge.id} className="p-6 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      {getStatusBadge(challenge.status)}
                      <h3 className="text-lg font-bold text-foreground dark:text-white mt-2">
                        {challenge.title || challenge.exam_name || 'Challenge'}
                      </h3>
                      <p className="text-sm text-muted-foreground">{challenge.exam_name}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-lg font-bold text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 px-3 py-1 rounded-lg">
                        {challenge.code}
                      </div>
                      {challenge.creator_id === user?.id && (
                        <span className="text-xs text-muted-foreground">You created this</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Zap size={16} />
                      <span>{challenge.num_questions} Qs</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock size={16} />
                      <span>{challenge.duration_minutes} min</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users size={16} />
                      <span>{challenge.participants?.length || 0} joined</span>
                    </div>
                  </div>

                  {/* Participants Preview */}
                  {challenge.participants && challenge.participants.length > 0 && (
                    <div className="mb-4 p-3 rounded-lg bg-muted/50 border border-border">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Participants</p>
                      <div className="flex flex-wrap gap-2">
                        {challenge.participants.slice(0, 5).map((participant, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs bg-background px-2 py-1 rounded-full">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-[10px] font-bold">
                              {participant.name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <span className="text-foreground">{participant.name}</span>
                            {participant.score !== null && participant.score !== undefined && (
                              <Badge className="bg-green-100 text-green-700 text-[10px] px-1">
                                {participant.score}%
                              </Badge>
                            )}
                          </div>
                        ))}
                        {challenge.participants.length > 5 && (
                          <span className="text-xs text-muted-foreground">+{challenge.participants.length - 5} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleViewChallenge(challenge.code)}
                      className="flex-1 gap-2"
                      variant={challenge.status === 'waiting' ? 'default' : 'outline'}
                    >
                      {challenge.status === 'waiting' ? (
                        <>
                          <Users size={16} />
                          View Lobby
                        </>
                      ) : challenge.status === 'active' ? (
                        <>
                          <Play size={16} />
                          Continue Test
                        </>
                      ) : (
                        <>
                          <Trophy size={16} />
                          View Results
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleCopyLink(challenge.code)}
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                    >
                      {copiedId === challenge.code ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <Share2 size={16} />
                      )}
                    </Button>
                    <Button
                      onClick={() => handleCopyCode(challenge.code)}
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                    >
                      {copiedId === challenge.code ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </Button>
                    {challenge.creator_id === user?.id && challenge.status !== 'completed' && (
                      <Button
                        onClick={() => handleDeleteChallenge(challenge.id, challenge.code)}
                        variant="outline"
                        size="icon"
                        className="shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                        title="Delete challenge"
                      >
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Completed Challenges */}
        {!loading && completedChallenges.length > 0 && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-foreground dark:text-white mb-6 flex items-center gap-3">
              <Trophy className="text-yellow-500" size={28} />
              Completed Challenges
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {completedChallenges.map(challenge => {
                const userResult = getUserResult(challenge);
                const sortedParticipants = [...(challenge.participants || [])].sort((a, b) => (b.score || 0) - (a.score || 0));
                const userRank = sortedParticipants.findIndex(p => p.user_id === user?.id) + 1;
                
                return (
                  <Card 
                    key={challenge.id} 
                    className="p-5 bg-card rounded-2xl border border-border hover:border-primary/50 transition-all cursor-pointer hover:shadow-lg"
                    onClick={() => handleViewCompletedStats(challenge)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-foreground dark:text-white truncate">
                          {challenge.title || challenge.exam_name || 'Challenge'}
                        </h3>
                        <p className="text-xs text-muted-foreground">{challenge.exam_name}</p>
                      </div>
                      {userRank > 0 && userRank <= 3 && (
                        <div className="ml-2">
                          {getRankIcon(userRank)}
                        </div>
                      )}
                    </div>

                    {/* User's Score */}
                    {userResult && (
                      <div className="mb-3 p-3 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Your Score</span>
                          <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {userResult.score !== null ? `${userResult.score}%` : 'N/A'}
                          </span>
                        </div>
                        {userRank > 0 && (
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-muted-foreground">Rank</span>
                            <span className="text-sm font-semibold text-foreground dark:text-white">
                              #{userRank} of {sortedParticipants.length}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users size={14} />
                        <span>{challenge.participants?.length || 0} players</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Zap size={14} />
                        <span>{challenge.num_questions} Qs</span>
                      </div>
                    </div>

                    <div className="mt-3 text-center">
                      <span className="text-xs text-primary">Click to view details →</span>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Stats Modal */}
        <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Trophy className="text-yellow-500" size={24} />
                Challenge Results
              </DialogTitle>
            </DialogHeader>

            {selectedChallenge && (
              <div className="space-y-4">
                {/* Challenge Info */}
                <div className="p-4 rounded-xl bg-muted/50 border border-border">
                  <h3 className="font-bold text-lg text-foreground dark:text-white">
                    {selectedChallenge.title || selectedChallenge.exam_name}
                  </h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Target size={14} />
                      {selectedChallenge.num_questions} Questions
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {selectedChallenge.duration_minutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {selectedChallenge.participants?.length || 0} players
                    </span>
                  </div>
                </div>

                {/* Leaderboard */}
                <div>
                  <h4 className="font-semibold text-foreground dark:text-white mb-3 flex items-center gap-2">
                    <BarChart3 size={18} className="text-primary" />
                    Leaderboard
                  </h4>
                  <div className="space-y-2">
                    {[...(selectedChallenge.participants || [])]
                      .sort((a, b) => (b.score || 0) - (a.score || 0))
                      .map((participant, index) => {
                        const isCurrentUser = participant.user_id === user?.id;
                        return (
                          <div 
                            key={participant.user_id || index}
                            className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                              isCurrentUser 
                                ? 'bg-purple-50 dark:bg-purple-950/30 border-purple-300 dark:border-purple-700' 
                                : 'bg-card border-border'
                            }`}
                          >
                            <div className="w-8 flex justify-center">
                              {getRankIcon(index + 1)}
                            </div>
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                              {participant.name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div className="flex-1">
                              <span className={`font-medium ${isCurrentUser ? 'text-purple-700 dark:text-purple-300' : 'text-foreground dark:text-white'}`}>
                                {participant.name || 'Unknown'}
                                {isCurrentUser && <span className="text-xs ml-1">(You)</span>}
                              </span>
                            </div>
                            <div className="text-right">
                              <span className={`font-bold text-lg ${
                                index === 0 ? 'text-yellow-600 dark:text-yellow-400' :
                                index === 1 ? 'text-gray-500' :
                                index === 2 ? 'text-amber-600' :
                                'text-foreground dark:text-white'
                              }`}>
                                {participant.score !== null && participant.score !== undefined ? `${participant.score}%` : 'DNF'}
                              </span>
                              {participant.time_taken && (
                                <p className="text-xs text-muted-foreground">
                                  {Math.floor(participant.time_taken / 60)}m {participant.time_taken % 60}s
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Winner Highlight */}
                {selectedChallenge.participants?.length > 0 && (
                  <div className="p-4 rounded-xl bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30 border border-yellow-300 dark:border-yellow-800 text-center">
                    <Award className="text-yellow-500 mx-auto mb-2" size={32} />
                    <p className="text-sm text-muted-foreground">Winner</p>
                    <p className="font-bold text-lg text-foreground dark:text-white">
                      🏆 {[...(selectedChallenge.participants || [])].sort((a, b) => (b.score || 0) - (a.score || 0))[0]?.name || 'Unknown'}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => handleViewChallenge(selectedChallenge.code)}
                    className="flex-1 gap-2"
                  >
                    <Trophy size={16} />
                    View Full Results
                  </Button>
                  <Button
                    onClick={() => setShowStatsModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Challenges;